/**
 * @packageDocumentation
 * @module f1/asynchronous
 */

import reduce from '../adt/reduce';
import task, { TTask } from './task';

export interface Tasks {
  /**
   * Run all asynchronous tasks in serial, each much succeed to invoke the next.
   *
   * @public
   * @return the final value from the list of tasks
   * @function f1.serial
   * @param tasks a list of tasks to execute
   * @example
   *
   * ```ts
   * f1.tasks([
   *   () => fetch('1.json'),
   *   (json1) => fetch(`${json1.key}.json`)
   * ], f1.skip).then((json2) => {
   *   // we receive one value
   * });
   * ```
   *
   */
  <A, B>(fns: [TTask<A, B>], value: A): Promise<B>;
  <A, B, C>(fns: [TTask<A, B>, TTask<B, C>], value: A): Promise<C>;
  <A, B, C, D>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>], value: A): Promise<D>;
  <A, B, C, D, E>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>], value: A): Promise<E>;
  <A, B, C, D, E, F>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>], value: A): Promise<F>;
  <A, B, C, D, E, F, G>(
    fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>],
    value: A
  ): Promise<G>;
  <A, B, C, D, E, F, G, H>(
    fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>, TTask<G, H>],
    value: A
  ): Promise<H>;
  <A, B, C, D, E, F, G, H, I>(
    fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>, TTask<G, H>, TTask<H, I>],
    value: A
  ): Promise<I>;
  <A, B, C, D, E, F, G, H, I, J>(
    fns: [
      TTask<A, B>,
      TTask<B, C>,
      TTask<C, D>,
      TTask<D, E>,
      TTask<E, F>,
      TTask<F, G>,
      TTask<G, H>,
      TTask<H, I>,
      TTask<I, J>
    ],
    value: A
  ): Promise<J>;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fns: [
      TTask<A, B>,
      TTask<B, C>,
      TTask<C, D>,
      TTask<D, E>,
      TTask<E, F>,
      TTask<F, G>,
      TTask<G, H>,
      TTask<H, I>,
      TTask<I, J>,
      TTask<J, K>
    ],
    value: A
  ): Promise<K>;

  <A, B>(fns: [TTask<A, B>]): (value: A) => Promise<B>;
  <A, B, C>(fns: [TTask<A, B>, TTask<B, C>]): (value: A) => Promise<C>;
  <A, B, C, D>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>]): (value: A) => Promise<D>;
  <A, B, C, D, E>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>]): (value: A) => Promise<E>;
  <A, B, C, D, E, F>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>]): (value: A) => Promise<F>;
  <A, B, C, D, E, F, G>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>]): (
    value: A
  ) => Promise<G>;
  <A, B, C, D, E, F, G, H>(
    fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>, TTask<G, H>]
  ): (value: A) => Promise<H>;
  <A, B, C, D, E, F, G, H, I>(
    fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>, TTask<G, H>, TTask<H, I>]
  ): (value: A) => Promise<I>;
  <A, B, C, D, E, F, G, H, I, J>(
    fns: [
      TTask<A, B>,
      TTask<B, C>,
      TTask<C, D>,
      TTask<D, E>,
      TTask<E, F>,
      TTask<F, G>,
      TTask<G, H>,
      TTask<H, I>,
      TTask<I, J>
    ]
  ): (value: A) => Promise<J>;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fns: [
      TTask<A, B>,
      TTask<B, C>,
      TTask<C, D>,
      TTask<D, E>,
      TTask<E, F>,
      TTask<F, G>,
      TTask<G, H>,
      TTask<H, I>,
      TTask<I, J>,
      TTask<J, K>
    ]
  ): (value: A) => Promise<K>;
}

/**
 * @see Tasks
 */
function tasks<A, B>(fns: [TTask<A, B>], value: A): Promise<B>;
function tasks<A, B, C>(fns: [TTask<A, B>, TTask<B, C>], value: A): Promise<C>;
function tasks<A, B, C, D>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>], value: A): Promise<D>;
function tasks<A, B, C, D, E>(fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>], value: A): Promise<E>;
function tasks<A, B, C, D, E, F>(
  fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>],
  value: A
): Promise<F>;
function tasks<A, B, C, D, E, F, G>(
  fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>],
  value: A
): Promise<G>;
function tasks<A, B, C, D, E, F, G, H>(
  fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>, TTask<G, H>],
  value: A
): Promise<H>;
function tasks<A, B, C, D, E, F, G, H, I>(
  fns: [TTask<A, B>, TTask<B, C>, TTask<C, D>, TTask<D, E>, TTask<E, F>, TTask<F, G>, TTask<G, H>, TTask<H, I>],
  value: A
): Promise<I>;
function tasks<A, B, C, D, E, F, G, H, I, J>(
  fns: [
    TTask<A, B>,
    TTask<B, C>,
    TTask<C, D>,
    TTask<D, E>,
    TTask<E, F>,
    TTask<F, G>,
    TTask<G, H>,
    TTask<H, I>,
    TTask<I, J>
  ],
  value: A
): Promise<J>;
function tasks<A, B, C, D, E, F, G, H, I, J, K>(
  fns: [
    TTask<A, B>,
    TTask<B, C>,
    TTask<C, D>,
    TTask<D, E>,
    TTask<E, F>,
    TTask<F, G>,
    TTask<G, H>,
    TTask<H, I>,
    TTask<I, J>,
    TTask<J, K>
  ],
  value: A
): Promise<K>;
function tasks<A, B, C, D, E, F, G, H, I, J, K>(
  tasks: TTask<A | B | C | D | E | F | G | H | I | J | K, A | B | C | D | E | F | G | H | I | J | K>[],
  value: A
): Promise<A | B | C | D | E | F | G | H | I | J | K> {
  return reduce(
    (chain, t) => chain.then((v) => task(t, v)),
    Promise.resolve(value) as Promise<A | B | C | D | E | F | G | H | I | J | K>,
    tasks
  ) as Promise<A | B | C | D | E | F | G | H | I | J | K>;
}

export default tasks;
